'use client';

import type * as z from 'zod';
import { addUpiPayment, verifyVPA } from '@/api/user';
import BottomFixedContainer from '@/components/bottom-fixed-container';
import { SuccessLottie } from '@/components/success-lottie';
import { Button } from '@/components/ui/button';
import { usePaymentMutations } from '@/hooks/query-hooks/use-add-payment';
import { addUpiSchema } from '@/lib/validations/bank-form-schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'nextjs-toploader/app';
import React from 'react';
import { useForm } from 'react-hook-form';
import ValidationStatus from './validation-status';
type FormValues = z.infer<typeof addUpiSchema>;
interface UpiFormProps {
  onBack?: () => void;
  onSuccessClose?: (status: boolean) => void;
}
export function UpiForm({
  onBack,
  onSuccessClose
}: UpiFormProps) {
  const router = useRouter();
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: {
      errors
    }
  } = useForm<FormValues>({
    resolver: zodResolver(addUpiSchema)
  });
  const {
    validate,
    addPayment,
    isValidating,
    isValid,
    isSuccess,
    verifyError,
    addPaymentError,
    name
  } = usePaymentMutations({
    verifyFn: verifyVPA,
    addPaymentFn: addUpiPayment,
    type: 'upi',
    onSuccessClose
  });
  React.useEffect(() => {
    if (verifyError) {
      setError('upiId', {
        type: 'manual',
        message: verifyError
      });
    }
    if (addPaymentError) {
      setError('root', {
        type: 'manual',
        message: addPaymentError
      });
    }
    if (isValid) {
      clearErrors('upiId');
    }
  }, [verifyError, addPaymentError, setError, clearErrors, isValid]);
  const onSubmit = (data: FormValues) => {
    if (!isValid) {
      setError('upiId', {
        type: 'manual',
        message: 'Invalid UPI ID'
      });
      return;
    }
    const upiData = {
      vpa: data.upiId,
      acceptTerms: data.privacyPolicy
    };
    addPayment(upiData);
  };
  return <div className="flex flex-col gap-4" data-sentry-component="UpiForm" data-sentry-source-file="upi-form.tsx">
      {isSuccess ? <div className="flex min-h-[80vh] w-full items-center justify-center px-0 md:min-h-[313px]">
              <SuccessLottie text="Your Account Linked Successfully" />
            </div> : <>
              <button type="button" onClick={onBack || (() => router.back())} className="flex  items-center gap-2">
                <ChevronLeft className="size-6" />
                <h1 className="text-xl font-semibold">UPI Details</h1>
              </button>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-3">
                <div className="space-y-4">
                  <div className="relative">
                    <label htmlFor="upiId" className="mb-1 block text-sm text-gray-600">Enter your UPI ID</label>
                    <input {...register('upiId')} onChange={event => {
              validate(event.target.value);
            }} className=" relative w-full rounded-lg border border-gray-200 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="example@ybl" />
                    <ValidationStatus hasError={!!errors.upiId} isValid={isValid} isValidating={isValidating} />
                  </div>
                  {name && <p className="mt-1 text-sm text-x-success-base">{name}</p>}
                  {errors.upiId && <p className="mt-1 text-sm text-red-500">{errors.upiId.message as string}</p>}

                  <div className="flex items-start gap-2">
                    <input type="checkbox" {...register('privacyPolicy')} className="mt-1" />
                    <label className="text-sm text-gray-600">
                      By continuing, you agree with the handling of your data as per our
                      {' '}
                      <Link href="/privacy-policy" target="_blank" className="text-x-accent-alpha">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  {errors.privacyPolicy && <p className="text-sm text-red-500">{errors.privacyPolicy.message as string}</p>}
                </div>
                {errors.root && <p className="text-sm text-red-500">{errors.root.message as string}</p>}
                <BottomFixedContainer>
                  <Button type="submit" className="w-full rounded-lg bg-blue-600 px-3 py-2 text-white transition-colors hover:bg-blue-700" disabled={!isValid || isValidating}>
                    Submit
                  </Button>
                </BottomFixedContainer>
              </form>
            </>}
    </div>;
}
;